import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { View, Text, StyleSheet, TextInput, Pressable, Icon2 } from '../lib/react-native-compat';
import { selectedJobAtom, jobUserDataAtom, jobCostDetailAtom, selectedJobDataAtom, selectedVersionAtom, jobPhaseCodesAtom, isLiveVersionAtom, phasedForecastAtom, projectCompleteAtom, dataSourceVersionAtom, selectedVersionLockedAtom } from '../store/company';
import { useAtom } from 'jotai';
import moment from 'moment';
import { updateUserDataArray, loadUserData, fNum, pNum } from '../db/company';
import { parseQtyMath, sharedUnit } from '../helpers/disciplineQty';
import xlsx from 'xlsx';

const s = (w = null) => StyleSheet.create({
    rate: {
        width: 60,
        height: 24,
        border: '1px solid #aeadaf',
        padding: 3,
        fontSize: 10,
    },
    rateB: {
        width: 60,
        height: 24,
        border: '1px solid white',
        padding: 3,
        fontSize: 10,
    },
    span: {
        width: 113,
        padding: 3,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
        fontSize: 10,
    },
    spanS: {
        width: 90,
        padding: 3,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
        fontSize: 10,
    },
    break: {
        width: 0,
        padding: 3,
        height: 24
    },
    total: {
        width: 83,
        border: '1px solid #aeadaf',
        textAlign: 'center',
        padding: 3,
        fontSize: 10,
    },
    title: {
        width: w ? Math.round(w * 0.75) : 75,
        border: '1px solid #aeadaf',
        textAlign: 'center',
        padding: 3,
        fontSize: 10,
    },
    cell: {
        width: w ? Math.round(w * 0.75) : 75,
        border: '1px solid #aeadaf',
        textAlign: 'right',
        padding: 3,
        fontSize: 10,
    }
})


// "How is this calculated" text for each Scorecard column. Keyed by the header label.
// The phase-row math lives in PhaseRow; the discipline-row math in DisciplineRow.
const SCORECARD_HELP = {
    'Quantities': {
        formula: 'Discipline rows roll up by Max, Sum, Row or a typed Qty',
        detail: 'A discipline row takes its quantities the way the Productivity Combined tab is set for that discipline: Max uses the phase with the largest quantity, Sum adds every phase, Row uses one chosen phase. Hours and dollars always add across the discipline\'s phases. The discipline\'s installed quantity is back-calculated from earned hours (each phase\'s % complete × its budget hours) so phases with different units can be combined.',
    },
    'Bid Budget': {
        formula: 'Σ Quantity on the Day Zero budget rows for this phase',
        detail: 'The quantity set up for this phase code in the Day Zero budget.',
    },
    'CO Log': {
        formula: 'Σ Quantity on Approved change orders for this phase',
        detail: 'Only change orders with status Approved count. Pending and rejected COs are left out.',
    },
    'UoM': {
        formula: 'Entered on this tab',
        detail: 'The unit of measure for this phase\'s quantities. Type it here; it is saved with the version.',
    },
    'Current Total': {
        formula: 'Bid Budget + CO Log',
        detail: 'The total quantity the phase is expected to install.',
    },
    'Installed (from Heavy Job) *New': {
        formula: 'Σ quantity on the HeavyJob (FP) job cost rows for this phase',
        detail: 'Quantities the field reported through HeavyJob, read from the job cost detail rows whose document type is FP. It updates on its own as HeavyJob posts.',
    },
    'Installed (Completed Quantities)': {
        formula: 'Entered on this tab',
        detail: 'Quantity installed that was not reported through HeavyJob. Type it here. The cell turns red when installed exceeds the Current Total.',
    },
    'Total Installed Quantity': {
        formula: 'Installed (from Heavy Job) + Installed (Completed Quantities)',
        detail: 'Everything installed so far, from both sources.',
    },
    'Remaining': {
        formula: 'Current Total − Total Installed Quantity',
        detail: 'Quantity still to install. Blank when the phase has no Current Total.',
    },
    '% C': {
        formula: 'Total Installed Quantity ÷ Current Total × 100',
        detail: 'Percent complete by quantity. Once the phase has a CPI and PF the Sensitivity columns project from them instead of the budget (see B or P).',
    },
    'Current Budget Dollars': {
        formula: 'Day Zero labor $ + Approved CO labor $',
        detail: 'Labor cost types 51100–51700 (518xx is left out). CO dollars come from the approved COs\' 51100–51700 columns.',
    },
    'Current Actuals Dollars': {
        formula: 'Σ Job_Cost on labor cost types (51xxx, not 518xx) for this phase',
        detail: 'Job-to-date actual labor dollars from the job cost detail for the version\'s data source.',
    },
    'Budget $ Utilization': {
        formula: 'Current Actuals Dollars ÷ Current Budget Dollars × 100',
        detail: 'How much of the labor dollar budget has been spent. Over 100% means the phase has spent more than its budget.',
    },
    'Current Budget Hours': {
        formula: 'Day Zero labor hours + Approved CO labor hours',
        detail: 'Labor hours on cost types 51xxx (not 518xx) in the Day Zero budget, plus labor hours on approved COs.',
    },
    'Current Actual Hours': {
        formula: 'Σ Total_Hours on labor cost types (51xxx, not 518xx) for this phase',
        detail: 'Job-to-date actual labor hours from the job cost detail.',
    },
    'Budget Hr Utilization': {
        formula: 'Current Actual Hours ÷ Current Budget Hours × 100',
        detail: 'How much of the labor hour budget has been used.',
    },
    'Budgeted $ / Qty': {
        formula: 'Current Budget Dollars ÷ Current Total',
        detail: 'The labor dollars the budget allows per unit installed.',
    },
    'Actual $ / Qty': {
        formula: 'Current Actuals Dollars ÷ Total Installed Quantity',
        detail: 'Labor dollars spent per unit installed so far. Installed counts both HeavyJob and typed quantities, so it lines up with % C and Remaining. A discipline row divides by its back-calculated installed quantity.',
    },
    'Forecast $ / Qty': {
        formula: 'Forecasted (Burn Tool) Dollars ÷ Remaining',
        detail: 'What the Burn Tool forecast implies per unit still to install.',
    },
    'Budgeted Hrs / Qty': {
        formula: 'Current Budget Hours ÷ Current Total',
        detail: 'The labor hours the budget allows per unit installed.',
    },
    'Actual Hrs / Qty': {
        formula: 'Current Actual Hours ÷ Total Installed Quantity',
        detail: 'Labor hours spent per unit installed so far. Installed counts both HeavyJob and typed quantities, so it lines up with % C and Remaining. A discipline row divides by its back-calculated installed quantity.',
    },
    'Forecast Hrs / Qty': {
        formula: 'Forecasted (Burn Tool) Hours ÷ Remaining',
        detail: 'What the Burn Tool forecast implies in hours per unit still to install.',
    },
    'Sensitivity Analysis': {
        formula: 'Burn Tool forecast vs. a projection from production so far',
        detail: 'Compares what the Burn Tool says is left (Forecasted) with what the phase\'s own rate to date says is left (Projected). A gap means the Burn Tool and the field\'s production disagree.',
    },
    'Forecasted (Burn Tool) Dollars': {
        formula: 'Σ over Burn Tool weeks: hours × rate × crew size',
        detail: 'The phase\'s labor ETC from the Burn Tool, adding only weeks inside both the version\'s start-to-complete window and the phase\'s start and end dates.',
    },
    'Projected (Production) Dollars': {
        formula: 'Current Budget $ ÷ (Actual) CPI − Current Actuals $. With no CPI yet: Current Budget $ − Actuals $',
        detail: 'Budget ÷ CPI is what the phase will cost at its performance so far; taking off the actuals leaves what is still to spend (B or P shows "p"). Until the phase has earned something and spent something there is no CPI, so the remaining budget is shown, blank if the budget is already spent.',
    },
    'B or P': {
        formula: '"p" when a CPI or PF exists, blank when the budget is used',
        detail: 'Says which basis the Projected column used: p = budget ÷ CPI (or ÷ PF) from production to date, blank = remaining budget.',
    },
    '(Under) / Over Forecasted $': {
        formula: 'Forecasted (Burn Tool) $ − Projected (Production) $',
        detail: 'Positive: the Burn Tool carries more than production suggests is needed. Negative, in parentheses: the Burn Tool carries less, so the phase may be under-forecast.',
    },
    '% (U)/O Tot EAC-Labor BT': {
        formula: '(Under) / Over ÷ Forecasted (Burn Tool) × 100',
        detail: 'The same gap as a share of the Burn Tool forecast.',
    },
    'Forecasted (Burn Tool) Hours': {
        formula: 'Σ over Burn Tool weeks: hours × crew size (or typed in, on a non-phased forecast)',
        detail: 'On a phased forecast this is the phase\'s labor hours ETC from the Burn Tool, same weeks as the dollars. On a regular (non-phased) forecast there is no per-phase Burn Tool, so you type the hours here and the bar above tracks what is still unallocated.',
    },
    'Projected (Production) Hours': {
        formula: 'Current Budget Hrs ÷ (Actual) PF − Current Actuals Hours. With no PF yet: Current Budget Hrs − Actual Hrs',
        detail: 'Same rule as Projected (Production) Dollars, in hours.',
    },
    '(Under) / Over Forecasted Hours': {
        formula: 'Forecasted (Burn Tool) Hours − Projected (Production) Hours',
        detail: 'Positive: the Burn Tool carries more hours than production suggests. Negative, in parentheses: it carries fewer.',
    },
    'Ratios': {
        formula: 'Above 1.00 is better than budget; below 1.00 is worse',
        detail: 'CPI compares dollars, PF compares hours. "Actual" looks at work done so far; "To Go" looks at the Burn Tool forecast for the rest. All four match the Overview tab.',
    },
    '(Actual) CPI $': {
        formula: 'Earned Labor $ ÷ Current Actuals Dollars, where Earned Labor $ = % C × Current Budget Dollars',
        detail: 'Cost performance index to date. % C is capped at 100%. Above 1.00 the phase is earning more than it spends; below 1.00 it is over budget. Same as the Overview tab.',
    },
    '(Actual) PF Hrs': {
        formula: 'Earned Labor Hrs ÷ Current Actuals Hours, where Earned Labor Hrs = % C × Current Budget Hours',
        detail: 'Productivity factor to date. % C is capped at 100%. Above 1.00 the crew is beating the budgeted hours; below 1.00 it is over on hours. Same as the Overview tab.',
    },
    '(To Go) CPI $': {
        formula: 'Budgeted $ / Qty ÷ Forecast $ / Qty',
        detail: 'How the Burn Tool\'s cost per remaining unit compares with the budget\'s. Above 1.00 the forecast is cheaper per unit than budget.',
    },
    '(To Go) PF Hrs': {
        formula: 'Budgeted Hrs / Qty ÷ Forecast Hrs / Qty',
        detail: 'How the Burn Tool\'s hours per remaining unit compare with the budget\'s. Above 1.00 the forecast is more productive than budget.',
    },
    'Notes': {
        formula: 'Entered on this tab',
        detail: 'Free text saved with the version.',
    },
}

// The small circled "i" next to a column title. Clicking opens the help popup for that column.
const InfoIcon = ({ onClick }) => (
    <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e) } }}
        title="How is this calculated?"
        aria-label="How is this calculated?"
        style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 3, cursor: 'pointer', color: 'var(--primary, #1d518c)', opacity: 0.75, lineHeight: 0 }}
    >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
    </span>
)

// The help popup. Rendered into document.body through a portal and fixed to the viewport, so the
// grid's scrolling, sticky columns and transformed ancestors can neither move nor clip it.
const ScorecardHelpPopup = ({ popup, onClose }) => {
    if (!popup) return null
    const help = SCORECARD_HELP[popup.key]
    if (!help) return null
    const width = 320
    const left = Math.max(8, Math.min(popup.left, window.innerWidth - width - 8))
    const top = Math.min(popup.top + 6, window.innerHeight - 40)
    return createPortal(
        <div
            className="scorecard-help-popup"
            role="dialog"
            aria-label={popup.key + ': how it is calculated'}
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'fixed', top, left, width, zIndex: 1000,
                background: 'var(--popover, #fff)', color: 'var(--foreground, #0a0a0a)',
                border: '1px solid var(--border, #e5e5e5)', borderRadius: 14,
                boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06))',
                padding: '12px 14px', fontSize: 12, lineHeight: 1.5, textAlign: 'left',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{popup.key}</div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--muted-foreground, #737373)', fontSize: 14, lineHeight: 1, padding: '0 2px' }}
                >×</button>
            </div>
            <div style={{ marginTop: 6, fontSize: 11, fontWeight: 500, color: 'var(--muted-foreground, #737373)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>How it is calculated</div>
            <div style={{ marginTop: 2, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{help.formula}</div>
            <div style={{ marginTop: 8, color: 'var(--text-secondary, #525252)' }}>{help.detail}</div>
        </div>,
        document.body
    )
}

const Scorecard = forwardRef((props, ref) => {
    const [phases, setPhases] = useState([{}, {}, {}])
    // Which column's "how is this calculated" popup is open, and where to draw it.
    const [helpPopup, setHelpPopup] = useState(null)
    const openHelp = (key) => (e) => {
        e.stopPropagation()
        const r = e.currentTarget.getBoundingClientRect()
        setHelpPopup(prev => prev && prev.key === key ? null : { key, top: r.bottom, left: r.left })
    }
    useEffect(() => {
        if (!helpPopup) return
        const onKey = (e) => { if (e.key === 'Escape') setHelpPopup(null) }
        const onClick = () => setHelpPopup(null)
        const onScroll = () => setHelpPopup(null)
        window.addEventListener('keydown', onKey)
        window.addEventListener('click', onClick)
        window.addEventListener('scroll', onScroll, true)
        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('click', onClick)
            window.removeEventListener('scroll', onScroll, true)
        }
    }, [helpPopup])
    // A column title with its info icon.
    const Th = ({ w, label, style, help }) => {
        const key = help || label
        return (
            <Text style={style ? [s(w).title, style] : s(w).title}>
                {label}
                {SCORECARD_HELP[key] ? <InfoIcon onClick={openHelp(key)} /> : null}
            </Text>
        )
    }
    const [phaseDescriptions, setPhaseDescriptions] = useState([])
    const [actualDollars, setActualDollars] = useState(0)
    const [actualHours, setActualHours] = useState(0)
    const [etcDollars, setEtcDollars] = useState({})
    const [etcHours, setEtcHours] = useState({})
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion, setSelectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData, setSelectedJobData] = useAtom(selectedJobDataAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [isLiveVersion, setIsLiveVersion] = useAtom(isLiveVersionAtom);
    const [regular, setRegular] = useAtom(phasedForecastAtom);
    const [projectComplete, setProjectComplete] = useAtom(projectCompleteAtom);
    const [versions, setVersions] = useAtom(dataSourceVersionAtom)
    const [lockedVersion, setLockedVersion] = useAtom(selectedVersionLockedAtom)
    const [phaseDayZero, setPhaseDayZero] = useState({})
    const [phaseCO, setPhaseCO] = useState({})
    const [projectWeeks, setProjectWeeks] = useState([])
    const [projectDates, setProjectDates] = useState([])
    const [userData, setUserData] = useState({})
    const [disciplines, setDisciplines] = useState({})
    const disciplinesRef = useRef({})
    const [sections, setSections] = useState([])
    const [math, setMath] = useState({})
    const [mathRow, setMathRow] = useState({})
    const localChanges = useRef([])
    const focusedId = useRef(null)
    const [versionInfo, setVersionInfo] = useState({})
    const rowsData = useRef({})
    const [jobPhaseCodes, setJobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [projectedBurnToolHours, setProjectedBurnToolHours] = useState(0);
    const projectedBurnToolHoursRef = useRef(0);
    const allocatedHoursRef = useRef({});
    const remainingDisplayRef = useRef(null);
    const allocatedDisplayRef = useRef(null);
    const projectedDisplayRef = useRef(null);
    const summaryBarRef = useRef(null);

    const handlePhaseRowData = (i, newRowData) => {
        const copyData = JSON.parse(JSON.stringify(rowsData.current));
        copyData[i] = newRowData;
        rowsData.current = copyData;
    };

    const updateRemainingDisplay = useCallback(() => {
        const projected = projectedBurnToolHoursRef.current;
        const allocated = Object.values(allocatedHoursRef.current).reduce((a, v) => a + v, 0);
        const remaining = projected - allocated;
        if (projectedDisplayRef.current) projectedDisplayRef.current.textContent = 'Projected Hours: ' + fNum(projected, 1);
        if (allocatedDisplayRef.current) allocatedDisplayRef.current.textContent = 'Allocated: ' + fNum(allocated, 1);
        if (remainingDisplayRef.current) {
            remainingDisplayRef.current.textContent = 'Remaining: ' + fNum(remaining, 1);
            remainingDisplayRef.current.style.color = remaining === 0 ? '#059669' : remaining > 0 ? '#d97706' : '#dc2626';
        }
        if (summaryBarRef.current) {
            summaryBarRef.current.style.backgroundColor = remaining === 0 ? '#f0fdf4' : remaining > 0 ? '#fffbeb' : '#fef2f2';
            summaryBarRef.current.style.borderColor = remaining === 0 ? '#bbf7d0' : remaining > 0 ? '#fde68a' : '#fecaca';
        }
    }, []);

    const handleForecastedHrsChange = useCallback((phase, hours) => {
        allocatedHoursRef.current[phase] = pNum(hours) || 0;
        updateRemainingDisplay();
    }, []);

    useImperativeHandle(ref, () => ({
        createExcel
    }));

    // Build the styled workbook server-side (matches the Productivity Combined export):
    // split discipline rollups vs phase detail, send real numbers, get a formatted .xlsx back.
    // Falls back to the plain local workbook if the styling service is unreachable.
    const createExcel = async () => {
        try {
            const isPhaseKey = (k) => typeof k === 'string' && k.includes('-');
            const entries = Object.entries(rowsData.current);
            const disciplineEntries = entries
                .filter(([k]) => !isPhaseKey(k))
                .sort((a, b) => Number(a[0]) - Number(b[0]));
            const phaseEntries = entries
                .filter(([k]) => isPhaseKey(k))
                .sort((a, b) => {
                    const [ai, aj] = a[0].split('-').map(Number);
                    const [bi, bj] = b[0].split('-').map(Number);
                    return (ai - bi) || (aj - bj);
                });

            // Cells arrive as comma-formatted strings; convert back to real numbers so the
            // workbook stores numbers (and the TOTAL row sums correctly). pNum turns
            // "(1,234)" into -1234; strip a trailing % for the percentage columns first.
            const num = (v) => (v === '' || v === null || v === undefined) ? '' : pNum(v);
            const pct = (v) => (v === '' || v === null || v === undefined) ? '' : pNum(String(v).replace('%', ''));

            const metrics = (row) => ({
                'Bid Budget': num(row['Day Zero Quantity']),
                'CO Log': num(row['CO Log']),
                'UoM': row['UOM'] || '',
                'Current Total': num(row['Current Total']),
                'Installed (from Heavy Job)': num(row['Installed (from Heavy Job)']),
                'Installed (Completed Quantities)': num(row['Installed (Completed Quantities)']),
                'Total Installed Quantity': num(row['Total Installed Quantity']),
                'Remaining': num(row['Remaining']),
                '% C': pct(row['%C']),
                'Current Budget Dollars': num(row['Current Budget Dollars']),
                'Current Actuals Dollars': num(row['Current Actuals Dollars']),
                'Budget $ Utilization': pct(row['Budget $ Utilization']),
                'Current Budget Hours': num(row['Current Budget Hours']),
                'Current Actual Hours': num(row['Current Actual Hours']),
                'Budget Hr Utilization': pct(row['Budget Hr Utilization']),
                'Budgeted $ / Qty': num(row['Budgeted $ / Qty']),
                'Actual $ / Qty': num(row['Actual $ / Qty']),
                'Forecast $ / Qty': num(row['Forecast $ / Qty']),
                'Budgeted Hrs / Qty': num(row['Budgeted Hrs / Qty']),
                'Actual Hrs / Qty': num(row['Actual Hrs / Qty']),
                'Forecast Hrs / Qty': num(row['Forecast Hrs / Qty']),
                'Forecasted (Burn Tool) Dollars': num(row['Forecasted (Burn Tool) Dollars']),
                'Projected (Production) Dollars': num(row['Projected (Production) Dollars']),
                'B or P (Dollars)': row['B or P (Dollars)'] || '',
                '(Under) / Over Forecasted $': num(row['(Under) / Over Forecasted $']),
                '% (U)/O Tot EAC-Labor BT (Dollars)': pct(row['% (U)/O Tot EAC-Labor BT (Dollars)']),
                'Forecasted (Burn Tool) Hours': num(row['Forecasted (Burn Tool) Hours']),
                'Projected (Production) Hours': num(row['Projected (Production) Hours']),
                'B or P (Hours)': row['B or P (Hours)'] || '',
                '(Under) / Over Forecasted Hours': num(row['(Under) / Over Forecasted Hours']),
                '% (U)/O Tot EAC-Labor BT (Hours)': pct(row['% (U)/O Tot EAC-Labor BT (Hours)']),
                '(Actual) CPI $': num(row['(Actual) CPI $']),
                '(Actual) PF Hrs': num(row['(Actual) PF Hrs']),
                '(To Go) CPI $': num(row['(To Go) CPI $']),
                '(To Go) PF Hrs': num(row['(To Go) PF Hrs']),
                'Notes': row['Notes'] || '',
            });

            const disciplineRows = disciplineEntries.map(([, row]) => ({
                'Description': row['Description'],
                'Phase Code': '',
                ...metrics(row),
            }));
            const phaseRows = phaseEntries.map(([, row]) => ({
                'Description': row['Description'],
                'Phase Code': row['Phase Code'],
                ...metrics(row),
            }));

            const ts = versionInfo?.Version_Start_DateTime
                ? moment(versionInfo.Version_Start_DateTime).format('M-D-YYYY') : '';
            const metadata = {
                jobId: selectedJob || '',
                jobName: selectedJobData?.BU_Description || selectedJobData?.Job_Description || '',
                versionTimestamp: ts,
            };

            const response = await fetch('https://forecastingtestpythonservice.azurewebsites.net/api/download_scorecard', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ disciplineRows, phaseRows, metadata }),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const blob = await response.blob();
            const filename = ['Scorecard', selectedJob, ts].filter(Boolean).join('_') + '.xlsx';
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Scorecard styled export failed, falling back to local export:', error);
            createExcelLocal();
        }
    };

    const createExcelLocal = () => {
        const listRows = Object.values(rowsData.current).map((row) => {
            return {
                'Description': row.Description,
                'Phase Code': row['Phase Code'],
                'Bid Budget': row['Day Zero Quantity'],
                'CO Log': row['CO Log'],
                'UoM': row['UOM'],
                'Current Total': row['Current Total'],
                'Installed (from Heavy Job)': row['Installed (from Heavy Job)'],
                'Installed (Completed Quantities)': row['Installed (Completed Quantities)'],
                'Total Installed Quantity': row['Total Installed Quantity'],
                'Remaining': row['Remaining'],
                '% C': row['%C'],
                'UoM (2)': row['UOM'],
                'Current Budget Dollars': row['Current Budget Dollars'],
                'Current Actuals Dollars': row['Current Actuals Dollars'],
                'Budget $ Utilization': row['Budget $ Utilization'],
                'Current Budget Hours': row['Current Budget Hours'],
                'Current Actual Hours': row['Current Actual Hours'],
                'Budget Hr Utilization': row['Budget Hr Utilization'],
                'Budgeted $ / Qty': row['Budgeted $ / Qty'],
                'Actual $ / Qty': row['Actual $ / Qty'],
                'Forecast $ / Qty': row['Forecast $ / Qty'],
                'Budgeted Hrs / Qty': row['Budgeted Hrs / Qty'],
                'Actual Hrs / Qty': row['Actual Hrs / Qty'],
                'Forecast Hrs / Qty': row['Forecast Hrs / Qty'],
                'Forecasted (Burn Tool) Dollars': row['Forecasted (Burn Tool) Dollars'],
                'Projected (Production) Dollars': row['Projected (Production) Dollars'],
                'B or P (Dollars)': row['B or P (Dollars)'],
                '(Under) / Over Forecasted $': row['(Under) / Over Forecasted $'],
                '% (U)/O Tot EAC-Labor BT (Dollars)': row['% (U)/O Tot EAC-Labor BT (Dollars)'],
                'Forecasted (Burn Tool) Hours': row['Forecasted (Burn Tool) Hours'],
                'Projected (Production) Hours': row['Projected (Production) Hours'],
                'B or P (Hours)': row['B or P (Hours)'],
                '(Under) / Over Forecasted Hours': row['(Under) / Over Forecasted Hours'],
                '% (U)/O Tot EAC-Labor BT (Hours)': row['% (U)/O Tot EAC-Labor BT (Hours)'],
                '(Actual) CPI $': row['(Actual) CPI $'],
                '(Actual) PF Hrs': row['(Actual) PF Hrs'],
                '(To Go) CPI $': row['(To Go) CPI $'],
                '(To Go) PF Hrs': row['(To Go) PF Hrs'],
                'Notes': row['Notes'],
            }
        });
        const ws = xlsx.utils.json_to_sheet(listRows);

        const wsColumns = [
            { wpx: 200 }, // Description
            { wpx: 100 }, // Phase Code
            { wpx: 100 }, // Bid Budget
            { wpx: 100 }, // CO Log
            { wpx: 50 },  // UoM
            { wpx: 100 }, // Current Total
            { wpx: 150 }, // Installed (from Heavy Job)
            { wpx: 150 }, // Installed (Completed Quantities)
            { wpx: 150 }, // Total Installed Quantity
            { wpx: 100 }, // Remaining
            { wpx: 50 },  // % C
            { wpx: 50 },  // UoM (2)
            { wpx: 120 }, // Current Budget Dollars
            { wpx: 120 }, // Current Actuals Dollars
            { wpx: 120 }, // Budget $ Utilization
            { wpx: 120 }, // Current Budget Hours
            { wpx: 120 }, // Current Actual Hours
            { wpx: 120 }, // Budget Hr Utilization
            { wpx: 100 }, // Budgeted $ / Qty
            { wpx: 100 }, // Actual $ / Qty
            { wpx: 100 }, // Forecast $ / Qty
            { wpx: 100 }, // Budgeted Hrs / Qty
            { wpx: 100 }, // Actual Hrs / Qty
            { wpx: 100 }, // Forecast Hrs / Qty
            { wpx: 140 }, // Forecasted (Burn Tool) Dollars
            { wpx: 140 }, // Projected (Production) Dollars
            { wpx: 80 },  // B or P (Dollars)
            { wpx: 140 }, // (Under) / Over Forecasted $
            { wpx: 120 }, // % (U)/O Tot EAC-Labor BT (Dollars)
            { wpx: 140 }, // Forecasted (Burn Tool) Hours
            { wpx: 140 }, // Projected (Production) Hours
            { wpx: 80 },  // B or P (Hours)
            { wpx: 140 }, // (Under) / Over Forecasted Hours
            { wpx: 120 }, // % (U)/O Tot EAC-Labor BT (Hours)
            { wpx: 100 }, // (Actual) CPI $
            { wpx: 100 }, // (Actual) PF Hrs
            { wpx: 100 }, // (To Go) CPI $
            { wpx: 100 }, // (To Go) PF Hrs
            { wpx: 300 }  // Notes
        ];
        ws['!cols'] = wsColumns;

        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'summary');
        xlsx.writeFile(wb, 'summary.xlsx');
    };

    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
    }, [versions, selectedVersion]);

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion, seth: true }).then((data) => {
                setJobUserData(data?.data);
            })
        }
    }, [selectedJob, selectedVersion])

    useEffect(() => {
        let completeDate = ''
        const uData = {
            dayZero: {},
            cos: {},
            burnTool: {},
            burnToolPhased: {},
            scorecard: {},
            productivity: {},
            math: {},
            mathRow: {}
        }
        if (jobUserData && jobUserData.length > 0) {
            let totalHours
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Burn Tool Labor') {
                    if (d.Field_Name === 'Total_Hours') {
                        totalHours = totalHours + (pNum(d.Forecast_Value) ? pNum(d.Forecast_String) : 0)
                    }
                    if (uData.burnTool[d?.Forecast_Row_ID]) {
                        uData.burnTool[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.burnTool[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                if (d.Forecast_Field_Name === 'completionDateForecast') {
                    completeDate = d.Forecast_Value
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    uData.burnToolPhased[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    uData.dayZero[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Change Order') {
                    uData.cos[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                }

                if (d?.Forecast_Tab === 'Scorecard') {
                    if (uData.scorecard[d?.Forecast_Row_ID]) {
                        uData.scorecard[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.scorecard[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                // Reuse the discipline mapping + rollup math chosen on the Productivity Combined tab
                if (d?.Forecast_Tab === 'Productivity Tracker') {
                    if (d?.Forecast_Field_Name === 'discipline') {
                        uData.productivity[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'math') {
                        uData.math[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'mathRow') {
                        uData.mathRow[d?.Forecast_Row_ID] = d.Forecast_Value
                    }
                }

            })

            const costTypeDayZero = {}
            Object.values(uData.dayZero).forEach((row) => {
                //console.log('dayZero', row)
                if (costTypeDayZero[row['Cost_Code']]) {
                    if (row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8') {
                        costTypeDayZero[row['Cost_Code']].hours += pNum(row['Hours']) ? pNum(row['Hours']) : 0
                        costTypeDayZero[row['Cost_Code']].dollars += pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0
                    }
                    costTypeDayZero[row['Cost_Code']].uom = row['UoM']
                    costTypeDayZero[row['Cost_Code']].quantities = costTypeDayZero[row['Cost_Code']].quantities + (isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']))
                } else {
                    costTypeDayZero[row['Cost_Code']] = {
                        hours: row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8' && row['Cost_Code'] ? pNum(row['Hours']) ? pNum(row['Hours']) : 0 : 0,
                        dollars: row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8' && row['Cost_Code'] ? pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0 : 0,
                        uom: row['UoM'] ? row['UoM'] : '',
                        quantities: row['Quantity'] ? (isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity'])) : 0
                    }
                }
            })
            console.log('costTypeDayZero', costTypeDayZero)

            const costTypeCos = {}
            Object.values(uData.cos).forEach((row) => {
                let phase = row.Cost_Code
                const isLaborHours = row.Forecast_Field_Name === 'totalLaborHours' ||
                    (String(row.Cost_Type || '').trim() === '51100' && row.Forecast_Field_Name !== 'totalEquipmentHours')
                const laborHours = isLaborHours ? (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours)) : 0
                if (costTypeCos[phase] && row.Status == 'Approved') {
                    costTypeCos[phase].hours = costTypeCos[phase].hours + laborHours
                    costTypeCos[phase].budgetQty = costTypeCos[phase].budgetQty + (isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity))
                    costTypeCos[phase].dollars = costTypeCos[phase].dollars
                        + (isNaN(pNum(row['51100']) ? 0 : pNum(row['51100'])))
                        + (isNaN(pNum(row['51200']) ? 0 : pNum(row['51200'])))
                        + (isNaN(pNum(row['51300']) ? 0 : pNum(row['51300'])))
                        + (isNaN(pNum(row['51400']) ? 0 : pNum(row['51400'])))
                        + (isNaN(pNum(row['51500']) ? 0 : pNum(row['51500'])))
                        + (isNaN(pNum(row['51600']) ? 0 : pNum(row['51600'])))
                        + (isNaN(pNum(row['51700']) ? 0 : pNum(row['51700'])))

                } else if (phase && row.Status == 'Approved') {
                    costTypeCos[phase] = {
                        hours: laborHours,
                        budgetQty: pNum(row.Quantity) ? (isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity)) : 0,
                        dollars: (isNaN(pNum(row['51100']) ? 0 : pNum(row['51100'])))
                            + (isNaN(pNum(row['51200']) ? 0 : pNum(row['51200'])))
                            + (isNaN(pNum(row['51300']) ? 0 : pNum(row['51300'])))
                            + (isNaN(pNum(row['51400']) ? 0 : pNum(row['51400'])))
                            + (isNaN(pNum(row['51500']) ? 0 : pNum(row['51500'])))
                            + (isNaN(pNum(row['51600']) ? 0 : pNum(row['51600'])))
                            + (isNaN(pNum(row['51700']) ? 0 : pNum(row['51700'])))
                    }
                }
            })
            console.log('costTypeCos', costTypeCos, uData.cos)

            console.log('uData', uData)
            const lHours = {}
            const lDollars = {}
            Object.keys(uData.burnToolPhased).forEach((row) => {
                const phasedLabor = uData.burnToolPhased
                const weekRow = row.includes('=')
                const week = row.split('=')[1]
                const phase = row.split('=')[0]
                const phaseDetails = phase ? phasedLabor[phase] : {}
                const withinProject = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(completeDate).endOf('week'))
                const withinStartStop = moment(week).isAfter(moment(phaseDetails?.Phase_Start).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.Phase_End).endOf('week'))
                const h = phasedLabor[row]?.Number_Hours ? phasedLabor[row].Number_Hours : 0
                const bR = phasedLabor[row]?.Rate ? phasedLabor[row].Rate : 0
                const cS = phasedLabor[row] && phasedLabor[row]?.Number_Units ? phasedLabor[row].Number_Units : 0
                //console.log('phasedLabor true?', versionInfo, projectComplete)
                if (weekRow && phasedLabor[row] && h && bR && cS && withinProject && withinStartStop) {
                    if (lDollars[phase]) {
                        lDollars[phase] = lDollars[phase] + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                    } else {
                        lDollars[phase] = (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                    }

                    if (lHours[phase]) {
                        lHours[phase] = lHours[phase] + (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    } else {
                        lHours[phase] = (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    }
                }
            })

            console.log('seth', lDollars, lHours)
            setEtcDollars(lDollars)
            setEtcHours(lHours)
            setPhaseDayZero(costTypeDayZero)
            setPhaseCO(costTypeCos)
            //console.log('uData', uData.scorecard)
            setUserData(uData.scorecard)
            setDisciplines(uData.productivity)
            setMath(uData.math)
            setMathRow(uData.mathRow)

            // Compute projected burn tool hours (future weeks only, matching sideMenu approach)
            const burnToolLabor = {};
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Burn Tool Labor') {
                    burnToolLabor[d?.Forecast_Row_ID] = d;
                }
            });
            let projectedHrs = 0;
            Object.values(burnToolLabor).forEach((row) => {
                const week = (row.Forecast_Row_ID || '').split('*')[0];
                const h = pNum(row.Number_Hours) || 0;
                const n = pNum(row.Number_Units) || 0;
                if (h > 0 && n > 0 && versionInfo?.Version_Start_DateTime &&
                    moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) &&
                    moment(projectComplete || completeDate).isAfter(week)) {
                    projectedHrs += h * n;
                }
            });
            setProjectedBurnToolHours(projectedHrs);
            projectedBurnToolHoursRef.current = projectedHrs;

            // Compute initial allocated hours from scorecard user data
            const initAlloc = {};
            Object.entries(uData.scorecard).forEach(([phase, data]) => {
                if (data.forecastedHrs) {
                    initAlloc[phase] = pNum(data.forecastedHrs) || 0;
                }
            });
            allocatedHoursRef.current = initAlloc;
        }
    }, [jobUserData, versionInfo])

    useEffect(() => {
        let dateSections = {}
        let uniqueWeeks = {}
        const plannedStart = moment(selectedJobData?.Planned_Start_Date)
        const plannedFinish = moment(selectedJobData?.Planned_Complete_Date)

        let currentDay = plannedStart
        let currentWeek = plannedStart.startOf('isoWeek')
        const days = plannedFinish.diff(plannedStart, 'days')
        for (let i = 0; i < days; i++) {
            const day = currentDay.clone()
            const week = currentDay.clone()
            day.add(i, 'days')
            week.add(i, 'days')
            currentWeek = week.startOf('isoWeek')
            const currentDayFormatted = day.format('YYYY-MM-DD')
            uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
            dateSections[currentDayFormatted] = [currentWeek.format('YYYY-MM-DD')]
        }

        setProjectDates(dateSections)
        setProjectWeeks(Object.keys(uniqueWeeks))
    }, [selectedJobData])

    useEffect(() => {
        console.log('phasae co', phaseCO)
        const phases = {}
        const heavyJobInstalled = {}
        
        // Filter for Heavy Job (FP) document type and group by phase
        Array.isArray(jobCostDetail) ? jobCostDetail?.forEach((row) => {
            if (!phases[row.Phase_Code] && row.Phase_Code) {
                phases[row.Phase_Code] = [row]
            } else if (row.Phase_Code) {
                phases[row.Phase_Code].push(row)
            }
            
            // Extract Heavy Job installed quantities from FP document type
            if (row.Document_Type === 'FP' && row.Phase_Code) {
                if (!heavyJobInstalled[row.Phase_Code]) {
                    heavyJobInstalled[row.Phase_Code] = 0
                }
                heavyJobInstalled[row.Phase_Code] += pNum(row.Total_Hours) || 0
            }
        }) : null

        Object.keys(phaseDayZero).forEach((p) => {
            if (!phases[p]) {
                phases[p] = [{ Phase_Code: p, Phase_Code_Description: jobPhaseCodes[p] }]
            }
        })

        Object.keys(phaseCO).forEach((p) => {
            if (!phases[p]) {
                phases[p] = [{ Phase_Code: p, Phase_Code_Description: jobPhaseCodes[p] }]
            }
        })

        const ph = Object.keys(phases)
        const phDescriptions = []
        ph.forEach((p) => {
            const code = phases[p][0].Phase_Code
            if (code != '1000' && code != '899999' && code != '899998' && code != '990000') {
                phDescriptions.push({
                    description: phases[p][0].Phase_Code_Description,
                    code: phases[p][0].Phase_Code,
                    dayZero: phaseDayZero[code]?.hours ? fNum(phaseDayZero[code]?.hours) : 0,
                    cos: phaseCO[code]?.hours ? fNum(phaseCO[code]?.hours) : 0,
                    dayZeroDollars: phaseDayZero[code]?.dollars ? fNum(phaseDayZero[code]?.dollars) : 0,
                    dayZeroHours: phaseDayZero[code]?.hours ? fNum(phaseDayZero[code]?.hours) : 0,
                    coDollars: phaseCO[code]?.dollars ? fNum(phaseCO[code]?.dollars) : 0,
                    coHours: phaseCO[code]?.hours ? fNum(phaseCO[code]?.hours) : 0,
                    dayZeroQuantity: phaseDayZero[code]?.quantities ? fNum(phaseDayZero[code]?.quantities) : 0,
                    coQuantity: phaseCO[code]?.budgetQty ? fNum(phaseCO[code]?.budgetQty) : 0,
                    heavyJobInstalled: heavyJobInstalled[code] ? fNum(heavyJobInstalled[code]) : 0
                })
            }
        })
        setPhases(phases)
        setPhaseDescriptions(phDescriptions)
    }, [jobCostDetail, phaseCO, phaseDayZero, etcDollars, jobPhaseCodes])

    // Group phases into discipline sections (mirrors Productivity Combined)
    useEffect(() => {
        disciplinesRef.current = disciplines
        const disciplineSections = {}
        phaseDescriptions.forEach((phase) => {
            phase.discipline = disciplines[phase.code] ? disciplines[phase.code] : 'Other'
            if (!disciplines[phase.code] && disciplineSections['Other']) {
                disciplineSections['Other'].push(phase)
            } else if (!disciplines[phase.code]) {
                disciplineSections['Other'] = [phase]
            } else if (disciplineSections[disciplines[phase.code]]) {
                disciplineSections[disciplines[phase.code]].push(phase)
            } else {
                disciplineSections[disciplines[phase.code]] = [phase]
            }
        })
        setSections(Object.values(disciplineSections))
    }, [disciplines, phaseDescriptions])


    const PhaseRow = (props) => {
        const index = props.index
        const identifier = props.identifier != null ? props.identifier : props.index
        const onSendToParent = props.onSendData
        let phaseActualHour = 0
        let phaseActualDollar = 0
        const rows = phases[props.phase?.code] ? phases[props.phase?.code] : []
        Array.isArray(rows) ? rows?.forEach((row) => {
            if (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8') {
                phaseActualDollar += pNum(row.Job_Cost)
                phaseActualHour += pNum(row.Total_Hours)
            }
        }) : null
        const phase = props.phase?.code
        const [expandEst, setExpandEst] = useState(true)
        const [expandAct, setExpandAct] = useState(true)
        const [code, setCode] = useState(props.phase?.code)
        const [codeDescription, setCodeDescription] = useState(props.phase?.description)
        const [phaseWeeks, setPhaseWeeks] = useState([])
        const [phaseActualDollars, setPhaseActualDollars] = useState(fNum(phaseActualDollar))
        const [phaseActualHours, setPhaseActualHours] = useState(fNum(phaseActualHour))
        const [expanded, setExpanded] = useState(false)
        const [dayZero, setDayZero] = useState(props.dayZero)
        const [co, setCo] = useState(props.cos)
        const [dayZeroDollars, setDayZeroDollars] = useState(props.phase?.dayZeroDollars)
        const [coDollars, setCoDollars] = useState(props.phase?.coDollars)
        const [dayZeroHours, setDayZeroHours] = useState(props.phase?.dayZeroHours)
        const [coHours, setCoHours] = useState(props.phase?.coHours)
        const [budgetDollars, setBudgetDollars] = useState(pNum(dayZeroDollars) + pNum(coDollars))
        const [budgetHours, setBudgetHours] = useState(pNum(dayZeroHours) + pNum(coHours))
        const [burnRateEst, setBurnRateEst] = useState(0)
        const [plug, setPlug] = useState(userData[props.phase?.code]?.plug)
        const [installed, setInstalled] = useState(userData[props.phase?.code]?.installed)
        const [uom, setUom] = useState(userData[props.phase?.code]?.uom)
        const [notes, setNotes] = useState(userData[props.phase?.code]?.notes)
        const [dayZeroQuantity, setDayZeroQuantity] = useState(props.phase?.dayZeroQuantity)
        const [coQuantity, setCoQuantity] = useState(props.phase?.coQuantity)
        const heavyJobInstalled = props.phase?.heavyJobInstalled || 0
        const totalInstalledQuantity = (isNaN(pNum(heavyJobInstalled)) ? 0 : pNum(heavyJobInstalled)) + (isNaN(pNum(installed)) ? 0 : pNum(installed))
        const totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity)) + (isNaN(pNum(plug)) ? 0 : pNum(plug))
        const remainingQuantity = totalQuantity > 0 ? (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) - totalInstalledQuantity : ''
        const percentComplete = totalQuantity > 0 ? (100 * totalInstalledQuantity) / pNum(totalQuantity) : ''
        const [budgetDollarsQty, setBudgetDollarsQty] = useState(totalQuantity ? fNum(budgetDollars / totalQuantity) : '')
        const [budgetHoursQty, setBudgetHoursQty] = useState(totalQuantity ? fNum(budgetHours / totalQuantity) : '')
        // Actual rates are per installed unit, counting HeavyJob installs as well as the typed quantity,
        // so they line up with % Complete / Remaining (which already include HeavyJob) and with the discipline rollup.
        const actualDollarsQty = totalInstalledQuantity > 0 ? fNum(pNum(phaseActualDollars) / totalInstalledQuantity) : ''
        const actualHoursQty = totalInstalledQuantity > 0 ? fNum(pNum(phaseActualHours) / totalInstalledQuantity) : ''
        const [localFocusedId, setLocalFocusedId] = useState(focusedId.current)
        // Unrounded budget hrs/qty for the To Go PF math (the displayed rate rounds to 2 decimals)
        const budgetHoursQtyRaw = totalQuantity ? budgetHours / totalQuantity : 0
        const forecastedRateDollars = etcDollars[phase] && remainingQuantity ? fNum(pNum(etcDollars[phase]) / remainingQuantity) : ''
        const forecastedRateHoursRaw = etcHours[phase] && remainingQuantity ? pNum(etcHours[phase]) / remainingQuantity : 0
        const forecastedRateHours = etcHours[phase] && remainingQuantity ? fNum(forecastedRateHoursRaw) : ''
        const forecastedDollars = etcDollars[phase] ? fNum(etcDollars[phase]) : ''
        const forecastedHours = etcHours[phase] ? fNum(etcHours[phase]) : ''
        const [forecastedHrs, setForecastedHrs] = useState(userData[props.phase?.code]?.forecastedHrs ? userData[props.phase?.code]?.forecastedHrs : '')
        // Earned labor = % Complete (capped at 100%) x budget, dollars and hours (same as the Overview tab)
        const earnedLaborDollars = percentComplete !== '' ? Math.min(pNum(percentComplete) / 100, 1) * pNum(budgetDollars) : 0
        const earnedLaborHours = percentComplete !== '' ? Math.min(pNum(percentComplete) / 100, 1) * pNum(budgetHours) : 0
        // Unrounded CPI / PF for the projection math
        const cpiRaw = earnedLaborDollars > 0 && pNum(phaseActualDollars) > 0 ? earnedLaborDollars / pNum(phaseActualDollars) : 0
        const pfRaw = earnedLaborHours > 0 && pNum(phaseActualHours) > 0 ? earnedLaborHours / pNum(phaseActualHours) : 0
        // Projected (Production) to-go = Budget / CPI (or Budget Hrs / PF) less the actuals to date,
        // i.e. the EAC at the phase's performance so far, minus what is already spent.
        // With no CPI yet (nothing installed or no actuals): budget less this phase's actuals.
        const projectedDollars = cpiRaw > 0 ? budgetDollars / cpiRaw - pNum(phaseActualDollars) : (budgetDollars - pNum(phaseActualDollars)) > 0 ? budgetDollars - pNum(phaseActualDollars) : ''
        const projectedHours = pfRaw > 0 ? budgetHours / pfRaw - pNum(phaseActualHours) : (budgetHours - pNum(phaseActualHours)) > 0 ? budgetHours - pNum(phaseActualHours) : ''
        const bOrP = cpiRaw > 0 || pfRaw > 0 ? 'p' : ''
        const inputsRef = useRef([])
        const [textTimeout, setTextTimeout] = useState(null)
        // Sensitivity: (Under)/Over = Forecasted (Burn Tool) - Projected (Production); signed so over-runs show in parens like the Scorecard workbook
        const overUnderDollars = (forecastedDollars || projectedDollars) ? fNum(pNum(forecastedDollars) - pNum(projectedDollars), 1) : ''
        const overUnderHours = (forecastedHours || projectedHours) ? fNum(pNum(forecastedHours) - pNum(projectedHours), 1) : ''
        const overUnderDollarsPercent = overUnderDollars !== '' && pNum(forecastedDollars) ? fNum((pNum(overUnderDollars) / pNum(forecastedDollars)) * 100, 1) + '%' : ''
        const overUnderHoursPercent = overUnderHours !== '' && pNum(forecastedHours) ? fNum((pNum(overUnderHours) / pNum(forecastedHours)) * 100, 1) + '%' : ''
        // Ratios: >1 = under budget, <1 = over budget
        // CPI = Earned Labor $ / Actual Labor $; PF = Earned Labor Hrs / Actual Labor Hrs (same as the Overview tab)
        const actualCpiDollars = cpiRaw > 0 ? fNum(cpiRaw) : ''
        const actualPfHours = pfRaw > 0 ? fNum(pfRaw) : ''
        const toGoCpiDollars = pNum(budgetDollarsQty) && pNum(forecastedRateDollars) ? fNum(pNum(budgetDollarsQty) / pNum(forecastedRateDollars)) : ''
        const toGoPfHours = budgetHoursQtyRaw > 0 && forecastedRateHoursRaw > 0 ? fNum(budgetHoursQtyRaw / forecastedRateHoursRaw) : ''

        useEffect(() => {
            const p = props.phase?.code
            if (focusedId.current == (p + '-burnPerHour')) {
                inputsRef.current[p + '-burnPerHour'].focus()
            } else if (focusedId.current == (p + '-numOfWorkers')) {
                inputsRef.current[p + '-numOfWorkers'].focus()
            } else if (focusedId.current == (p + '-hoursPerWeek')) {
                inputsRef.current[p + '-hoursPerWeek'].focus()
            } else if (focusedId.current == (p + '-notes')) {
                inputsRef.current[p + '-notes'].focus()
            } else if (focusedId.current == (p + '-notes')) {
                inputsRef.current[p + '-notes'].focus()
            } else if (focusedId.current == (p + '-forecastedHrs')) {
                inputsRef.current[p + '-forecastedHrs'].focus()
            }
        }, [localFocusedId])

        // Track allocated hours for the remaining indicator
        useEffect(() => {
            if (props.onForecastedHrsChange && phase) {
                props.onForecastedHrsChange(phase, forecastedHrs);
            }
        }, [forecastedHrs]);

        const updateLocalChanges = async (field, value) => {
            console.log('scorecard change', field, value, phase)
            if (selectedJob && value && value != 'undefined') {
                let data = {
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: field,
                    tab: "Scorecard",
                    rowId: phase,
                    version: selectedVersion + '',
                    value: value + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion
                }
                console.log('saving data s', data)
                updateUserDataArray(data)

                const stateUpdate = {
                    Forecast_Field_Name: field,
                    Forecast_Value: value,
                    Forecast_Row_ID: phase,
                    Forecast_Tab: "Scorecard",
                    BU_ID: selectedJob,
                    key: phase
                }

                let isRecorded = false
                let lChanges = {}

                localChanges.current.forEach((d) => {
                    if (d.Forecast_Row_ID == phase && d.Forecast_Field_Name == field && d.Forecast_Tab == "Scorecard") {
                        isRecorded = true
                        d.Forecast_Value = value
                    }
                    lChanges[phase] = { ...d, key: d.Forecast_Row_ID + '*' + d.Forecast_Field_Name }
                })

                if (!isRecorded) {
                    lChanges[phase] = stateUpdate
                }

                localChanges.current = Object.values(lChanges)
            }
        }

        const updateState = () => {
            let lChanges = {}
            localChanges.current.forEach((d) => {
                lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name] = { ...d, key: d.Forecast_Row_ID + '*' + d.Forecast_Field_Name }
            })

            const uData = JSON.parse(JSON.stringify(jobUserData))
            uData.forEach((d) => {
                if (lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name] && d.Forecast_Tab == "Scorecard") {
                    d.Forecast_Value = lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name].Forecast_Value
                    lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name].updated = true
                }
            })

            const notUpdated = Object.values(lChanges).filter((d) => !lChanges[d.key].updated)
            console.log('not updated', notUpdated)
            setJobUserData([...uData, ...notUpdated])
        }

        useEffect(() => {
            const rowData = {
                'Description': codeDescription,
                'Phase Code': code,
                'Day Zero Quantity': dayZeroQuantity,
                'CO Log': coQuantity,
                'Plug': plug,
                'UOM': uom,
                'Current Total': totalQuantity,
                'Installed (from Heavy Job)': heavyJobInstalled,
                'Installed (Completed Quantities)': installed,
                'Total Installed Quantity': totalInstalledQuantity,
                'Remaining': remainingQuantity,
                '%C': percentComplete ? fNum(percentComplete, 1) + '%' : '',
                'Current Budget Dollars': pNum(dayZeroDollars) + pNum(coDollars) > 0 ? fNum(pNum(dayZeroDollars) + pNum(coDollars)) : '',
                'Current Actuals Dollars': phaseActualDollars,
                'Budget $ Utilization': phaseActualDollars && (dayZeroDollars || coDollars) ? fNum(100 * pNum(phaseActualDollars) / (pNum(dayZeroDollars) + pNum(coDollars)), 1) + '%' : '',
                'Current Budget Hours': pNum(dayZeroHours) + pNum(coHours) > 0 ? fNum(pNum(dayZeroHours) + pNum(coHours)) : '',
                'Current Actual Hours': phaseActualHours,
                'Budget Hr Utilization': phaseActualHours && (dayZeroHours || coHours) ? fNum(100 * pNum(phaseActualHours) / (pNum(dayZeroHours) + pNum(coHours)), 1) + '%' : '',
                'Budgeted $ / Qty': budgetDollarsQty,
                'Actual $ / Qty': actualDollarsQty,
                'Forecast $ / Qty': forecastedRateDollars,
                'Budgeted Hrs / Qty': budgetHoursQty,
                'Actual Hrs / Qty': actualHoursQty,
                'Forecast Hrs / Qty': forecastedRateHours,
                'Forecasted (Burn Tool) Dollars': forecastedDollars ? fNum(pNum(forecastedDollars), 1) : '',
                'Projected (Production) Dollars': projectedDollars ? fNum(pNum(projectedDollars), 1) : '',
                'B or P (Dollars)': bOrP,
                '(Under) / Over Forecasted $': overUnderDollars,
                '% (U)/O Tot EAC-Labor BT (Dollars)': overUnderDollarsPercent,
                'Forecasted (Burn Tool) Hours': regular == 'regular' ? forecastedHrs : (forecastedHours ? fNum(pNum(forecastedHours), 1) : ''),
                'Projected (Production) Hours': projectedHours ? fNum(pNum(projectedHours), 1) : '',
                'B or P (Hours)': bOrP,
                '(Under) / Over Forecasted Hours': overUnderHours,
                '% (U)/O Tot EAC-Labor BT (Hours)': overUnderHoursPercent,
                '(Actual) CPI $': actualCpiDollars,
                '(Actual) PF Hrs': actualPfHours,
                '(To Go) CPI $': toGoCpiDollars,
                '(To Go) PF Hrs': toGoPfHours,
                'Notes': notes,
            }

            onSendToParent(identifier, rowData);
        }, [props])

        return (
            <View style={{ flexDirection: 'row' }}>
                <View style={{ flexDirection: 'row', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10}}>
                    <Text style={[s(200).cell, {backgroundColor: 'white'}]}>{codeDescription}</Text>
                    <Text style={[s(100).cell, {backgroundColor: 'white'}]}>{code}</Text>
                </View>
                <View style={{ width: 15 }}></View>
                <Text style={s(100).cell}>{fNum(pNum(dayZeroQuantity), 1)}</Text>
                <Text style={s(100).cell}>{fNum(pNum(coQuantity), 1)}</Text>
                {/* <View style={{ position: 'relative' }}
                <View style={{ width: 3, height: 28, backgroundColor: '#808080', left: 5, top: 4, position: 'absolute', zIndex: 2 }}></View>
                <TextInput style={s(100).cell}
                    value={plug}
                    ref={e => { inputsRef.current[phase + '-plug'] = e }}
                    onFocus={() => { focusedId.current = phase + '-plug' }}  // Track the input when focused
                    onChangeText={t => {
                        setPlug(t)
                        clearTimeout(textTimeout)
                        updateLocalChanges('plug', t)
                        setLocalFocusedId(phase + '-plug')
                        if (t.length > 0) {
                            setTextTimeout(setTimeout(() => {
                                updateState()
                            }, 1000))
                        }
                    }}
                ></TextInput>
            </View>             */}
                <View>
                    <View style={{ width: 2, height: 21, backgroundColor: '#808080', left: 4, top: 3, position: 'absolute', zIndex: 2 }}></View>
                    <TextInput style={s(50).cell}
                        value={uom}
                        ref={e => { inputsRef.current[phase + '-uom'] = e }}
                        onFocus={() => { focusedId.current = phase + '-uom' }}  // Track the input when focused
                        onChangeText={t => {
                            setUom(t)
                            clearTimeout(textTimeout)
                            updateLocalChanges('uom', t)
                            setLocalFocusedId(phase + '-uom')
                            if (t.length > 0) {
                                setTextTimeout(setTimeout(() => {
                                    updateState()
                                }, 1000))
                            }
                        }}
                    ></TextInput>
                </View>
                <Text style={s(100).cell}>{totalQuantity > 0 ? fNum(totalQuantity) : ''}</Text>
                <Text style={s(100).cell}>{heavyJobInstalled ? fNum(pNum(heavyJobInstalled), 1) : ''}</Text>
                <View>
                    <View style={{ width: 2, height: 21, backgroundColor: '#808080', left: 4, top: 3, position: 'absolute', zIndex: 2 }}></View>
                    <TextInput style={[s(100).cell, { backgroundColor: installed > totalQuantity ? 'red' : '' }]}
                        value={installed}
                        ref={e => { inputsRef.current[phase + '-installed'] = e }}
                        onFocus={() => { focusedId.current = phase + '-installed' }}  // Track the input when focused
                        onChangeText={t => {
                            setInstalled(t)
                            clearTimeout(textTimeout)
                            updateLocalChanges('installed', t)
                            setLocalFocusedId(phase + '-installed')
                            if (t.length > 0) {
                                setTextTimeout(setTimeout(() => {
                                    updateState()
                                }, 1000))
                            }
                        }}
                    ></TextInput>
                </View>
                <Text style={s(100).cell}>{totalInstalledQuantity > 0 ? fNum(totalInstalledQuantity, 1) : ''}</Text>
                <Text style={[s(100).cell, { backgroundColor: installed > totalQuantity ? 'red' : '' }]}>{remainingQuantity}</Text>
                <Text style={s(50).cell}>{percentComplete ? fNum(percentComplete, 1) + '%' : ''}</Text>
                <Text style={s(50).cell}>{uom}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={s(100).cell}>{pNum(dayZeroDollars) + pNum(coDollars) > 0 ? fNum(pNum(dayZeroDollars) + pNum(coDollars)) : ''}</Text>
                <Text style={s(100).cell}>{phaseActualDollars}</Text>
                <Text style={s(100).cell}>{phaseActualDollars && (dayZeroDollars || coDollars) ? fNum(100 * pNum(phaseActualDollars) / (pNum(dayZeroDollars) + pNum(coDollars)), 1) + '%' : ''}</Text>
                <View style={{ width: 4 }}></View>
                <Text style={s(100).cell}>{pNum(dayZeroHours) + pNum(coHours) > 0 ? fNum(pNum(dayZeroHours) + pNum(coHours)) : ''}</Text>
                <Text style={s(100).cell}>{phaseActualHours}</Text>
                <Text style={s(100).cell}> {phaseActualHours && (dayZeroHours || coHours) ? fNum(100 * pNum(phaseActualHours) / (pNum(dayZeroHours) + pNum(coHours)), 1) + '%' : ''}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={s(100).cell}>{budgetDollarsQty}</Text>
                <Text style={s(100).cell}>{actualDollarsQty}</Text>
                <Text style={s(100).cell}>{forecastedRateDollars}</Text>
                <View style={{ width: 4 }}></View>
                <Text style={s(100).cell}>{budgetHoursQty}</Text>
                <Text style={s(100).cell}>{actualHoursQty}</Text>
                <Text style={s(100).cell}>{forecastedRateHours}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={s(100).cell}>{forecastedDollars ? fNum(pNum(forecastedDollars), 1) : ''}</Text>
                <Text style={s(100).cell}>{projectedDollars ? fNum(pNum(projectedDollars), 1) : ''}</Text>
                <Text style={s(50).cell}>{bOrP}</Text>
                <Text style={s(100).cell}>{overUnderDollars}</Text>
                <Text style={s(50).cell}>{overUnderDollarsPercent}</Text>
                <View style={{ width: 4 }}></View>
                {regular == 'regular' ?
                    <View>
                        <View style={{ width: 2, height: 21, backgroundColor: '#808080', left: 4, top: 3, position: 'absolute', zIndex: 2 }}></View>
                        <TextInput style={s(100).cell}
                            value={forecastedHrs}
                            ref={e => { inputsRef.current[phase + '-forecastedHrs'] = e }}
                            onFocus={() => { focusedId.current = phase + '-forecastedHrs' }}  // Track the input when focused
                            onChangeText={t => {
                                setForecastedHrs(t)
                                clearTimeout(textTimeout)
                                updateLocalChanges('forecastedHrs', t)
                                setLocalFocusedId(phase + '-forecastedHrs')
                                if (t.length > 0) {
                                    setTextTimeout(setTimeout(() => {
                                        updateState()
                                    }, 1000))
                                }
                            }}
                        ></TextInput>
                    </View> :
                    <Text style={s(100).cell}>{forecastedHours ? fNum(pNum(forecastedHours), 1) : ''}</Text>
                }
                <Text style={s(100).cell}>{projectedHours ? fNum(pNum(projectedHours), 1) : ''}</Text>
                <Text style={s(50).cell}>{bOrP}</Text>
                <Text style={s(100).cell}>{overUnderHours}</Text>
                <Text style={s(50).cell}>{overUnderHoursPercent}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={s(100).cell}>{actualCpiDollars}</Text>
                <Text style={s(100).cell}>{actualPfHours}</Text>
                <Text style={s(100).cell}>{toGoCpiDollars}</Text>
                <Text style={s(100).cell}>{toGoPfHours}</Text>
                <View>
                    <View style={{ width: 2, height: 21, backgroundColor: '#808080', left: 4, top: 3, position: 'absolute', zIndex: 2 }}></View>
                    <TextInput style={s(400).cell}
                        value={notes}
                        ref={e => { inputsRef.current[phase + '-notes'] = e }}
                        onFocus={() => { focusedId.current = phase + '-notes' }}  // Track the input when focused
                        onChangeText={t => {
                            setNotes(t)
                            clearTimeout(textTimeout)
                            updateLocalChanges('notes', t)
                            setLocalFocusedId(phase + '-notes')
                            if (t.length > 0) {
                                setTextTimeout(setTimeout(() => {
                                    updateState()
                                }, 1000))
                            }
                        }}
                    ></TextInput>
                </View>
            </View >
        )
    }

    const DisciplineRow = (props) => {
        const index = props.index
        const discipline = props.phase?.discipline
        const sectionPhases = sections && sections[index] ? sections[index] : []
        const onSendToParent = props.onSendData
        const status = math[discipline] ? math[discipline] : ''
        const mRow = mathRow[discipline] ? mathRow[discipline] : ''

        // Quantity rollup mirrors Productivity Combined (Max / Sum / Row). Read-only here.
        let uom = ''
        let dayZeroQuantity = 0
        let coQuantity = 0
        if (status == 'Row') {
            const row = sectionPhases[pNum(mRow) - 1]
            if (row) {
                uom = userData[row.code]?.uom
                dayZeroQuantity = pNum(row?.dayZeroQuantity)
                coQuantity = pNum(row?.coQuantity)
            }
        } else if (status == 'Qty') {
            // Typed discipline quantity ("11 EA"), see productivityCombined.
            const q = parseQtyMath(mRow)
            const withUom = sectionPhases.find((p) => userData[p.code]?.uom)
            uom = q.uom || (withUom ? userData[withUom.code].uom : '')
            dayZeroQuantity = q.qty
            coQuantity = 0
        } else if (status == 'Sum') {
            sectionPhases.forEach((p) => {
                dayZeroQuantity += (isNaN(pNum(p.dayZeroQuantity)) ? 0 : pNum(p.dayZeroQuantity))
                coQuantity += (isNaN(pNum(p.coQuantity)) ? 0 : pNum(p.coQuantity))
            })
            // The unit the phases carrying a quantity share, see productivityCombined.
            uom = sharedUnit(sectionPhases
                .filter((p) => (pNum(p.dayZeroQuantity) || 0) + (pNum(p.coQuantity) || 0) > 0)
                .map((p) => userData[p.code]?.uom))
        } else { // default Max
            let max = 0
            let maxIndex = 0
            sectionPhases.forEach((p, i) => {
                if (pNum(p.coQuantity) + pNum(p.dayZeroQuantity) > max) {
                    max = pNum(p.coQuantity) + pNum(p.dayZeroQuantity)
                    maxIndex = i
                }
            })
            const maxPhase = sectionPhases[maxIndex]?.code
            uom = userData[maxPhase]?.uom
            dayZeroQuantity = pNum(sectionPhases[maxIndex]?.dayZeroQuantity)
            coQuantity = pNum(sectionPhases[maxIndex]?.coQuantity)
        }
        const totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity))

        // Hours / dollars / actuals always sum across the discipline's phases
        let dayZeroHours = 0
        let coHours = 0
        let dayZeroDollars = 0
        let coDollars = 0
        let phaseActualHours = 0
        let phaseActualDollars = 0
        let earnedHours = 0
        let forecastedHours = 0
        let forecastedDollars = 0

        sectionPhases.forEach((p) => {
            let phaseActualHour = 0
            let phaseActualDollar = 0
            const rows = phases[p.code] ? phases[p.code] : []
            Array.isArray(rows) ? rows?.forEach((row) => {
                if (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8') {
                    phaseActualDollar += pNum(row.Job_Cost)
                    phaseActualHour += pNum(row.Total_Hours)
                }
            }) : null

            dayZeroHours += pNum(p.dayZeroHours)
            coHours += pNum(p.coHours)
            dayZeroDollars += pNum(p.dayZeroDollars)
            coDollars += pNum(p.coDollars)
            phaseActualHours += phaseActualHour
            phaseActualDollars += phaseActualDollar

            // Earned hours per phase = % complete * phase budget hours (back-calc avoids summing mixed-UoM quantities)
            const installedQ = userData[p?.code]?.installed && !isNaN(pNum(userData[p?.code]?.installed)) ? (pNum(userData[p?.code]?.installed) + pNum(p.heavyJobInstalled)) : pNum(p.heavyJobInstalled)
            const plug = userData[p?.code]?.plug
            const totalQuantityQ = (isNaN(pNum(p.dayZeroQuantity)) ? 0 : pNum(p.dayZeroQuantity)) + (isNaN(pNum(p.coQuantity)) ? 0 : pNum(p.coQuantity)) + (isNaN(pNum(plug)) ? 0 : pNum(plug))
            const budgetHoursQ = pNum(p.dayZeroHours) + pNum(p.coHours)
            const percentCompleteQ = totalQuantityQ > 0 ? (isNaN(pNum(installedQ)) ? 0 : 100 * pNum(installedQ)) / pNum(totalQuantityQ) : ''
            earnedHours += (percentCompleteQ ? pNum(percentCompleteQ) * pNum(budgetHoursQ) / 100 : 0)

            forecastedHours += regular == 'regular' ? (isNaN(pNum(userData[p.code]?.forecastedHrs)) ? 0 : pNum(userData[p.code]?.forecastedHrs)) : (isNaN(pNum(etcHours[p.code])) ? 0 : pNum(etcHours[p.code]))
            forecastedDollars += (isNaN(pNum(etcDollars[p.code])) ? 0 : pNum(etcDollars[p.code]))
        })

        const budgetHours = pNum(dayZeroHours) + pNum(coHours)
        const budgetDollars = pNum(dayZeroDollars) + pNum(coDollars)
        // Back-calc installed quantity from earned hours so % complete works across mixed UoMs
        const installed = budgetHours && pNum(budgetHours) > 0 ? fNum(pNum(earnedHours) / pNum(budgetHours) * pNum(totalQuantity)) : ''
        const totalInstalledQuantity = isNaN(pNum(installed)) ? 0 : pNum(installed)
        const percentComplete = totalQuantity > 0 ? (100 * pNum(totalInstalledQuantity)) / pNum(totalQuantity) : ''
        const remainingQuantity = totalQuantity > 0 ? pNum(totalQuantity) - pNum(totalInstalledQuantity) : ''

        const budgetDollarsQty = totalQuantity ? fNum(budgetDollars / totalQuantity) : ''
        // Unrounded rates for the PF math (see PhaseRow).
        const budgetHoursQtyRaw = totalQuantity ? budgetHours / totalQuantity : 0
        const budgetHoursQty = totalQuantity ? fNum(budgetHoursQtyRaw) : ''
        const actualDollarsQty = installed && pNum(installed) > 0 ? fNum(pNum(phaseActualDollars) / pNum(installed)) : ''
        const actualHoursQtyRaw = installed && pNum(installed) > 0 ? pNum(phaseActualHours) / pNum(installed) : 0
        const actualHoursQty = installed && pNum(installed) > 0 ? fNum(actualHoursQtyRaw) : ''
        const forecastedRateDollars = forecastedDollars && remainingQuantity ? fNum(pNum(forecastedDollars) / pNum(remainingQuantity)) : ''
        const forecastedRateHoursRaw = forecastedHours && remainingQuantity ? pNum(forecastedHours) / pNum(remainingQuantity) : 0
        const forecastedRateHours = forecastedHours && remainingQuantity ? fNum(forecastedRateHoursRaw) : ''

        const budgetDU = phaseActualDollars && budgetDollars ? fNum(100 * pNum(phaseActualDollars) / pNum(budgetDollars), 1) + '%' : ''
        const budgetHU = phaseActualHours && budgetHours ? fNum(100 * pNum(phaseActualHours) / pNum(budgetHours), 1) + '%' : ''

        // Earned labor and unrounded CPI / PF, then Projected (Production) = Budget / CPI less actuals (mirror PhaseRow)
        const earnedLaborDollars = percentComplete !== '' ? Math.min(pNum(percentComplete) / 100, 1) * pNum(budgetDollars) : 0
        const earnedLaborHours = percentComplete !== '' ? Math.min(pNum(percentComplete) / 100, 1) * pNum(budgetHours) : 0
        const cpiRaw = earnedLaborDollars > 0 && pNum(phaseActualDollars) > 0 ? earnedLaborDollars / pNum(phaseActualDollars) : 0
        const pfRaw = earnedLaborHours > 0 && pNum(phaseActualHours) > 0 ? earnedLaborHours / pNum(phaseActualHours) : 0
        const projectedDollars = cpiRaw > 0 ? budgetDollars / cpiRaw - phaseActualDollars : (budgetDollars - phaseActualDollars) > 0 ? budgetDollars - phaseActualDollars : ''
        const projectedHours = pfRaw > 0 ? budgetHours / pfRaw - phaseActualHours : (budgetHours - phaseActualHours) > 0 ? budgetHours - phaseActualHours : ''
        const bOrP = cpiRaw > 0 || pfRaw > 0 ? 'p' : ''

        // Sensitivity + Ratios rollups (mirror PhaseRow)
        const overUnderDollars = (forecastedDollars || projectedDollars) ? fNum(pNum(forecastedDollars) - pNum(projectedDollars), 1) : ''
        const overUnderHours = (forecastedHours || projectedHours) ? fNum(pNum(forecastedHours) - pNum(projectedHours), 1) : ''
        const overUnderDollarsPercent = overUnderDollars !== '' && pNum(forecastedDollars) ? fNum((pNum(overUnderDollars) / pNum(forecastedDollars)) * 100, 1) + '%' : ''
        const overUnderHoursPercent = overUnderHours !== '' && pNum(forecastedHours) ? fNum((pNum(overUnderHours) / pNum(forecastedHours)) * 100, 1) + '%' : ''
        // CPI = Earned Labor $ / Actual Labor $; PF = Earned Labor Hrs / Actual Labor Hrs (same as the Overview tab)
        const actualCpiDollars = cpiRaw > 0 ? fNum(cpiRaw) : ''
        const actualPfHours = pfRaw > 0 ? fNum(pfRaw) : ''
        const toGoCpiDollars = pNum(budgetDollarsQty) && pNum(forecastedRateDollars) ? fNum(pNum(budgetDollarsQty) / pNum(forecastedRateDollars)) : ''
        const toGoPfHours = budgetHoursQtyRaw > 0 && forecastedRateHoursRaw > 0 ? fNum(budgetHoursQtyRaw / forecastedRateHoursRaw) : ''

        const mathLabel = status == 'Row' ? ('Row ' + mRow) : status == 'Qty' ? ('Qty ' + mRow) : (status ? status : 'Max')

        useEffect(() => {
            const rowData = {
                'Description': discipline,
                'Phase Code': '',
                'Day Zero Quantity': dayZeroQuantity,
                'CO Log': coQuantity,
                'UOM': uom,
                'Current Total': totalQuantity,
                'Installed (from Heavy Job)': '',
                'Installed (Completed Quantities)': '',
                'Total Installed Quantity': totalInstalledQuantity,
                'Remaining': remainingQuantity,
                '%C': percentComplete ? fNum(percentComplete, 1) + '%' : '',
                'Current Budget Dollars': budgetDollars > 0 ? fNum(budgetDollars) : '',
                'Current Actuals Dollars': fNum(phaseActualDollars),
                'Budget $ Utilization': budgetDU,
                'Current Budget Hours': budgetHours > 0 ? fNum(budgetHours) : '',
                'Current Actual Hours': fNum(phaseActualHours),
                'Budget Hr Utilization': budgetHU,
                'Budgeted $ / Qty': budgetDollarsQty,
                'Actual $ / Qty': actualDollarsQty,
                'Forecast $ / Qty': forecastedRateDollars,
                'Budgeted Hrs / Qty': budgetHoursQty,
                'Actual Hrs / Qty': actualHoursQty,
                'Forecast Hrs / Qty': forecastedRateHours,
                'Forecasted (Burn Tool) Dollars': forecastedDollars ? fNum(pNum(forecastedDollars), 1) : '',
                'Projected (Production) Dollars': projectedDollars ? fNum(pNum(projectedDollars), 1) : '',
                'B or P (Dollars)': bOrP,
                '(Under) / Over Forecasted $': overUnderDollars,
                '% (U)/O Tot EAC-Labor BT (Dollars)': overUnderDollarsPercent,
                'Forecasted (Burn Tool) Hours': forecastedHours ? fNum(pNum(forecastedHours), 1) : '',
                'Projected (Production) Hours': projectedHours ? fNum(pNum(projectedHours), 1) : '',
                'B or P (Hours)': bOrP,
                '(Under) / Over Forecasted Hours': overUnderHours,
                '% (U)/O Tot EAC-Labor BT (Hours)': overUnderHoursPercent,
                '(Actual) CPI $': actualCpiDollars,
                '(Actual) PF Hrs': actualPfHours,
                '(To Go) CPI $': toGoCpiDollars,
                '(To Go) PF Hrs': toGoPfHours,
                'Notes': '',
            }
            onSendToParent(index, rowData);
        }, [props])

        const dCell = (w) => [s(w).cell, { backgroundColor: '#efefef', fontWeight: 600 }]

        return (
            <View style={{ flexDirection: 'row' }}>
                <View style={{ flexDirection: 'row', position: 'sticky', left: 0, backgroundColor: '#efefef', zIndex: 10 }}>
                    <Text style={dCell(200)}>{discipline}</Text>
                    <Text style={dCell(100)}>{mathLabel}</Text>
                </View>
                <View style={{ width: 15 }}></View>
                <Text style={dCell(100)}>{dayZeroQuantity ? fNum(pNum(dayZeroQuantity), 1) : ''}</Text>
                <Text style={dCell(100)}>{coQuantity ? fNum(pNum(coQuantity), 1) : ''}</Text>
                <Text style={dCell(50)}>{uom}</Text>
                <Text style={dCell(100)}>{totalQuantity > 0 ? fNum(totalQuantity) : ''}</Text>
                <Text style={dCell(100)}></Text>
                <Text style={dCell(100)}></Text>
                <Text style={dCell(100)}>{totalInstalledQuantity > 0 ? fNum(totalInstalledQuantity, 1) : ''}</Text>
                <Text style={dCell(100)}>{remainingQuantity ? fNum(pNum(remainingQuantity), 1) : ''}</Text>
                <Text style={dCell(50)}>{percentComplete ? fNum(percentComplete, 1) + '%' : ''}</Text>
                <Text style={dCell(50)}>{uom}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={dCell(100)}>{budgetDollars > 0 ? fNum(budgetDollars) : ''}</Text>
                <Text style={dCell(100)}>{phaseActualDollars ? fNum(phaseActualDollars) : ''}</Text>
                <Text style={dCell(100)}>{budgetDU}</Text>
                <View style={{ width: 4 }}></View>
                <Text style={dCell(100)}>{budgetHours > 0 ? fNum(budgetHours) : ''}</Text>
                <Text style={dCell(100)}>{phaseActualHours ? fNum(phaseActualHours) : ''}</Text>
                <Text style={dCell(100)}>{budgetHU}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={dCell(100)}>{budgetDollarsQty}</Text>
                <Text style={dCell(100)}>{actualDollarsQty}</Text>
                <Text style={dCell(100)}>{forecastedRateDollars}</Text>
                <View style={{ width: 4 }}></View>
                <Text style={dCell(100)}>{budgetHoursQty}</Text>
                <Text style={dCell(100)}>{actualHoursQty}</Text>
                <Text style={dCell(100)}>{forecastedRateHours}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={dCell(100)}>{forecastedDollars ? fNum(pNum(forecastedDollars), 1) : ''}</Text>
                <Text style={dCell(100)}>{projectedDollars ? fNum(pNum(projectedDollars), 1) : ''}</Text>
                <Text style={dCell(50)}>{bOrP}</Text>
                <Text style={dCell(100)}>{overUnderDollars}</Text>
                <Text style={dCell(50)}>{overUnderDollarsPercent}</Text>
                <View style={{ width: 4 }}></View>
                <Text style={dCell(100)}>{forecastedHours ? fNum(pNum(forecastedHours), 1) : ''}</Text>
                <Text style={dCell(100)}>{projectedHours ? fNum(pNum(projectedHours), 1) : ''}</Text>
                <Text style={dCell(50)}>{bOrP}</Text>
                <Text style={dCell(100)}>{overUnderHours}</Text>
                <Text style={dCell(50)}>{overUnderHoursPercent}</Text>
                <View style={{ width: 15 }}></View>
                <Text style={dCell(100)}>{actualCpiDollars}</Text>
                <Text style={dCell(100)}>{actualPfHours}</Text>
                <Text style={dCell(100)}>{toGoCpiDollars}</Text>
                <Text style={dCell(100)}>{toGoPfHours}</Text>
                <Text style={dCell(400)}></Text>
            </View>
        )
    }

    return (
        <View style={{ padding: 8 }}>
            {regular == 'regular' && projectedBurnToolHours > 0 && (
                <View ref={summaryBarRef} style={{
                    flexDirection: 'row',
                    backgroundColor: '#fffbeb',
                    padding: 8,
                    paddingLeft: 12,
                    paddingRight: 12,
                    borderRadius: 5,
                    marginBottom: 6,
                    alignItems: 'center',
                    alignSelf: 'flex-end',
                    marginRight: 165,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#fde68a',
                }}>
                    <Text ref={projectedDisplayRef} style={{ fontSize: 10, color: '#334155', fontWeight: '600', marginRight: 12 }}>
                        Projected Hours: 0
                    </Text>
                    <Text ref={allocatedDisplayRef} style={{ fontSize: 10, color: '#334155', marginRight: 12 }}>
                        Allocated: 0
                    </Text>
                    <Text ref={remainingDisplayRef} style={{
                        fontSize: 10,
                        fontWeight: '700',
                        color: '#d97706'
                    }}>
                        Remaining: 0
                    </Text>
                </View>
            )}
            {/* The card is as wide as the grid and the page scrolls sideways (nothing
                is clipped inside the card). The inner wrapper is as wide as the widest
                row so no cell shrinks (every cell carries a fixed width, and the header
                and data rows must agree). */}
            <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 15, marginBottom: 75, marginRight: 150, width: 'max-content' }}>
            <View style={{ width: 'max-content', minWidth: '100%' }}>
                {/* Both header rows stay pinned to the top of the scroll area while the
                    phase rows scroll under them. The wrapper carries its own white fill
                    (the title cells are transparent) and sits above the rows' sticky
                    Description / Phase Code columns. */}
                <View style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: 'white' }}>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={s(300).title}></Text>
                    <View style={{ width: 15 }}></View>
                    <Th w={850} label="Quantities" />
                    <View style={{ width: 15 }}></View>
                    <Th w={605} label="B&A (Budget & Actuals)" />
                    <View style={{ width: 15 }}></View>
                    <Th w={605} label="Dollar/Hour Quantity Analysis" />
                    <View style={{ width: 15 }}></View>
                    <Th w={810} label="Sensitivity Analysis" />
                    <View style={{ width: 15 }}></View>
                    <Th w={400} label="Ratios" />
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ flexDirection: 'row', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}>
                        <Th w={200} label="Description" style={{backgroundColor: 'white'}} />
                        <Th w={100} label="Phase Code" style={{backgroundColor: 'white'}} />
                    </View>
                    <View style={{ width: 15 }}></View>
                    <Th w={100} label="Bid Budget" />
                    <Th w={100} label="CO Log" />
                    <Th w={50} label="UoM" />
                    <Th w={100} label="Current Total" />
                    <Th w={100} label="Installed (from Heavy Job) *New" style={{backgroundColor: 'lightgreen'}} />
                    <Th w={100} label="Installed (Completed Quantities)" />
                    <Th w={100} label="Total Installed Quantity" />
                    <Th w={100} label="Remaining" />
                    <Th w={50} label="% C" />
                    <Th w={50} label="UoM" />
                    <View style={{ width: 15 }}></View>
                    <Th w={100} label="Current Budget Dollars" />
                    <Th w={100} label="Current Actuals Dollars" />
                    <Th w={100} label="Budget $ Utilization" />
                    <View style={{ width: 4 }}></View>
                    <Th w={100} label="Current Budget Hours" />
                    <Th w={100} label="Current Actual Hours" />
                    <Th w={100} label="Budget Hr Utilization" />
                    <View style={{ width: 15 }}></View>
                    <Th w={100} label="Budgeted $ / Qty" />
                    <Th w={100} label="Actual $ / Qty" />
                    <Th w={100} label="Forecast $ / Qty" />
                    <View style={{ width: 4 }}></View>
                    <Th w={100} label="Budgeted Hrs / Qty" />
                    <Th w={100} label="Actual Hrs / Qty" />
                    <Th w={100} label="Forecast Hrs / Qty" />
                    <View style={{ width: 15 }}></View>
                    <Th w={100} label="Forecasted (Burn Tool) Dollars" />
                    <Th w={100} label="Projected (Production) Dollars" />
                    <Th w={50} label="B or P" />
                    <Th w={100} label="(Under) / Over Forecasted $" />
                    <Th w={50} label="% (U)/O Tot EAC-Labor BT" />
                    <View style={{ width: 4 }}></View>
                    <Th w={100} label="Forecasted (Burn Tool) Hours" />
                    <Th w={100} label="Projected (Production) Hours" />
                    <Th w={50} label="B or P" />
                    <Th w={100} label="(Under) / Over Forecasted Hours" />
                    <Th w={50} label="% (U)/O Tot EAC-Labor BT" />
                    <View style={{ width: 15 }}></View>
                    <Th w={100} label="(Actual) CPI $" />
                    <Th w={100} label="(Actual) PF Hrs" />
                    <Th w={100} label="(To Go) CPI $" />
                    <Th w={100} label="(To Go) PF Hrs" />
                    <Th w={400} label="Notes" />
                </View>
                </View>
                {sections.map((phases, i) => (
                    <View key={i}>
                        <DisciplineRow phase={phases[0]} index={i} onSendData={handlePhaseRowData} />
                        {phases.map((phase, j) => (
                            <View key={j}>
                                <PhaseRow phase={phase} index={j} identifier={i + '-' + j} onSendData={handlePhaseRowData} onForecastedHrsChange={handleForecastedHrsChange} />
                            </View>
                        ))}
                    </View>
                ))}
            </View>
            </View>
            <ScorecardHelpPopup popup={helpPopup} onClose={() => setHelpPopup(null)} />
        </View>
    );
});

export default Scorecard;


